<!-- Make Recurring toggle -->
<div class="recurrence-toggle-section">
  <mat-slide-toggle
    [(ngModel)]="isRecurring"
    (ngModelChange)="onToggleRecurring()"
    [disabled]="recurrenceGateUnmet">
    Make Recurring
  </mat-slide-toggle>
  <span *ngIf="recurrenceGateUnmet" class="recurrence-hint">{{ recurrenceGateHint }}</span>
</div>

<!-- Recurrence options (shown when toggled on) -->
<div *ngIf="isRecurring" class="recurrence-options" @slideFade>
  <div class="row">

    <!-- Left column: config fields -->
    <div class="col-md-6">
      <!-- Days -->
      <div class="recurrence-section">
        <label class="recurrence-label">Repeat on</label>
        <div class="day-toggle-section">
          <button *ngFor="let day of dayOptions" type="button"
                  class="day-toggle-btn" [class.selected]="isDaySelected(day.value)"
                  (click)="toggleDay(day.value)">
            {{ day.label }}
            <span *ngIf="hasDayOverride(day.value)" class="day-override-dot"></span>
          </button>
        </div>
      </div>

      <!-- Frequency -->
      <div class="recurrence-section">
        <mat-button-toggle-group [(ngModel)]="repetitionUnits"
                                 (ngModelChange)="emitConfig()" appearance="standard">
          <mat-button-toggle value="weekly">Weekly</mat-button-toggle>
          <mat-button-toggle value="eow">Every Other Week</mat-button-toggle>
          <mat-button-toggle value="monthly">Monthly</mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      <!-- End Condition -->
      <div class="recurrence-section">
        <mat-radio-group [(ngModel)]="endMode" (ngModelChange)="onEndModeChange()"
                         class="stop-condition-group">
          <mat-radio-button value="date">Until date</mat-radio-button>
          <mat-radio-button *ngIf="supportsEnrollment" value="enrollment">Until enrollment ends</mat-radio-button>
          <mat-radio-button value="indefinite">Indefinite</mat-radio-button>
        </mat-radio-group>
        <mat-form-field *ngIf="endMode === 'date'" style="width: 180px; margin-left: 12px;">
          <mat-label>End Date</mat-label>
          <input #endDateInput matInput type="text"
                 [value]="endDateDisplayValue"
                 readonly
                 (click)="openEndDatePicker()">
          <button matSuffix mat-icon-button type="button" (click)="openEndDatePicker()">
            <mat-icon>calendar_today</mat-icon>
          </button>
        </mat-form-field>
        <div *ngIf="endMode === 'enrollment'" class="enrollment-end-info">
          <ng-container *ngIf="selectedEnrollment">
            <span *ngIf="enrollmentLessonsAvailable > 0" class="enrollment-available">
              {{ enrollmentLessonsAvailable }} of {{ enrollmentLessonsTotal }} available ({{ enrollmentLessonsUsed }} used), will generate {{ enrollmentLessonsAvailable }} event{{ enrollmentLessonsAvailable !== 1 ? 's' : '' }}
            </span>
            <span *ngIf="enrollmentLessonsAvailable === 0" class="enrollment-none">
              Enrollment fully used ({{ enrollmentLessonsUsed }} of {{ enrollmentLessonsTotal }} used)
            </span>
          </ng-container>
          <span *ngIf="!selectedEnrollment" class="enrollment-none">
            Select an enrollment first
          </span>
        </div>
      </div>

      <!-- Alternate Instructors -->
      <div class="recurrence-section">
        <div class="alternate-instructors-row">
          <mat-form-field style="flex: 1">
            <mat-label>Alternate Instructors (optional)</mat-label>
            <mat-select [(ngModel)]="alternateInstructors" (ngModelChange)="onAlternatesChanged()" multiple>
              <ng-container *ngFor="let element of sortedInstructors">
                <mat-option *ngIf="element.status !== '0' && element.uid !== instructorId"
                            [value]="element.uid">{{ element.value }}</mat-option>
              </ng-container>
            </mat-select>
          </mat-form-field>
          <button *ngIf="alternateInstructors.length > 0" #rotationBtn
                  mat-icon-button type="button"
                  matTooltip="Rotation Order"
                  (click)="openRotationPopover(rotationBtn._elementRef.nativeElement)">
            <mat-icon>swap_vert</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Right column: preview grid -->
    <div class="col-md-6">
      <div *ngIf="previewEvents.length > 0" class="recurrence-preview-section">
        <div class="recurrence-preview-header" style="display: flex; align-items: center; justify-content: space-between;">
          <span>Appointments to create ({{ activePreviewCount }}<span *ngIf="endMode === 'indefinite'"> now, &infin; total</span><span *ngIf="excludedDates.size > 0">, {{ excludedDates.size }} skipped</span>)</span>
          <button type="button" class="preview-layout-toggle" (click)="togglePreviewLayout()" [title]="previewLayout === 'grid' ? 'Switch to list view' : 'Switch to grid view'">
            <mat-icon>{{ previewLayout === 'grid' ? 'view_list' : 'grid_view' }}</mat-icon>
          </button>
        </div>
        <div class="recurrence-preview-scroll">
          <div class="event-blocks-grid" [class.event-blocks-list]="previewLayout === 'list'">
            <div *ngFor="let evt of previewEvents" #card class="event-block"
                 [class.event-block-conflict]="isEventConflict(evt) && !isEventExcluded(evt)"
                 [class.event-block-excluded]="isEventExcluded(evt)"
                 [class.event-block-selected]="isEventSelected(evt)"
                 (click)="selectEvent(evt, card)">

              <!-- Skip/restore button -->
              <button type="button" class="event-block-skip-btn"
                      [class.restore]="isEventExcluded(evt)"
                      (click)="toggleExclude(evt, $event)"
                      [title]="isEventExcluded(evt) ? 'Restore' : 'Skip'">
                <mat-icon>{{ isEventExcluded(evt) ? 'undo' : 'close' }}</mat-icon>
              </button>

              <!-- Conflict icon -->
              <div class="event-block-conflict-icon" *ngIf="isEventConflict(evt) && !isEventExcluded(evt)">
                <mat-icon>error</mat-icon>
              </div>

              <!-- Override indicator -->
              <div class="event-block-override-dot" *ngIf="hasDateOverride(evt.date) && !isEventExcluded(evt)"></div>

              <div class="event-block-day">{{ evt.day }}</div>
              <div class="event-block-datestr">{{ formatEventDate(evt.date) }}</div>
              <div class="event-block-time">{{ evt.time }}</div>
              <div class="event-block-instructor">{{ evt.instructor }}</div>
              <div class="event-block-meta">{{ evt.duration }}m</div>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="previewEvents.length === 0 && days.length > 0" class="recurrence-preview-empty">
        No appointments in the selected date range.
      </div>
    </div>

  </div>
</div>

<!-- CDK Overlay popover template for editing a single event -->
<ng-template #eventPopoverTpl>
  <div class="event-popover-content" *ngIf="selectedEvent">
    <div class="event-popover-header">
      <strong>Edit {{ selectedEvent.day }}, {{ formatEventDate(selectedEvent.date) }}</strong>
      <span class="event-popover-hint">Changes apply to this event only</span>
    </div>
    <div class="event-popover-fields">
      <mat-form-field style="width: 100%;">
        <mat-label>Time</mat-label>
        <mat-select [(ngModel)]="editTime" (ngModelChange)="onEditTimeChange()">
          <mat-option *ngFor="let t of timeOptions" [value]="t.value">{{ t.label }}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field style="width: 100%;">
        <mat-label>Instructor</mat-label>
        <mat-select [(ngModel)]="editInstructor" (ngModelChange)="onEditInstructorChange()">
          <ng-container *ngFor="let element of sortedInstructors">
            <mat-option *ngIf="element.status !== '0'"
                        [value]="element.uid">{{ element.value }}</mat-option>
          </ng-container>
        </mat-select>
      </mat-form-field>

      <app-schedule-conflicts
        #popoverConflicts
        [compact]="true"
        [dateTime]="editDateTime"
        [instructor]="editInstructor"
        [duration]="duration"
        (dateTimeChange)="onPopoverAlternativeTime($event)">
      </app-schedule-conflicts>
    </div>
    <div class="event-popover-actions">
      <button mat-raised-button color="primary" type="button" (click)="saveEventEdit()">
        Apply
      </button>
      <button mat-button type="button" (click)="cancelEdit()">Cancel</button>
    </div>
  </div>
</ng-template>

<!-- CDK Overlay popover for rotation order -->
<ng-template #rotationPopoverTpl>
  <div class="event-popover-content">
    <div class="event-popover-header">
      <strong>Rotation Order</strong>
      <span class="event-popover-hint">Drag to reorder</span>
    </div>
    <div class="rotation-order-list" cdkDropList (cdkDropListDropped)="onRotationReorder($event)">
      <div class="rotation-order-item rotation-order-primary">
        <span class="rotation-order-number">1</span>
        <span class="rotation-order-name">{{ getInstructorName(instructorId) }}</span>
        <span class="rotation-order-badge">Primary</span>
      </div>
      <div *ngFor="let uid of alternateInstructors; let i = index"
           class="rotation-order-item" cdkDrag>
        <mat-icon class="rotation-order-handle" cdkDragHandle>drag_indicator</mat-icon>
        <span class="rotation-order-number">{{ i + 2 }}</span>
        <span class="rotation-order-name">{{ getInstructorName(uid) }}</span>
        <button mat-icon-button class="rotation-order-remove"
                (click)="removeAlternate(i)" matTooltip="Remove">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  </div>
</ng-template>
